// REL-036-039 — deploy-mid-session stale-chunk recovery.
//
// A lazy route's chunk hashes vanish when a new build deploys; the dynamic
// import then fails ('Failed to fetch dynamically imported module' / Safari's
// 'Importing a module script failed'). App.tsx's lazyWithReload catches that and
// calls this to decide what to do. Extracted from App.tsx so the decision +
// loop-guard are unit-testable; all side-effects are injected.
//
// The sequence (order is load-bearing):
//   1. Offline → rethrow. A reload can't fetch the chunk, only flashes the
//      screen; the ErrorBoundary shows a message instead, and the chunk loads
//      once back online.
//   2. Reloaded for this same reason within the window → rethrow + clear the
//      gate. The chunk really is missing; looping the reload helps nobody.
//   3. Otherwise → record the attempt, activate any waiting SW first (a bare
//      reload re-serves the OLD precached index.html under the still-controlling
//      old SW and fails identically), falling back to a plain reload, and tell
//      the caller to hang on the Suspense fallback until navigation kicks in.

export const CHUNK_RELOAD_WINDOW_MS = 60_000
const RELOAD_AT_KEY = 'kf49_chunk_reload_at'

export interface ChunkReloadDeps {
  now: () => number
  online: () => boolean
  getReloadAt: () => string | null
  setReloadAt: (value: string) => void
  clearReloadAt: () => void
  reload: () => void
  // Activates a waiting service worker so the reload navigates through the fresh
  // build; resolves false when there's nothing to activate (not a deploy swap).
  recoverStaleBundle: () => Promise<boolean>
}

export type ChunkReloadDecision = 'rethrow' | 'reloading'

export function handleChunkLoadError(deps: ChunkReloadDeps): ChunkReloadDecision {
  if (!deps.online()) return 'rethrow'

  const now = deps.now()
  const last = Number(deps.getReloadAt() || '0')
  if (now - last < CHUNK_RELOAD_WINDOW_MS) {
    deps.clearReloadAt()
    return 'rethrow'
  }

  deps.setReloadAt(String(now))
  // The reload fallback is UNCONDITIONAL (idle-wake audit B3): if the
  // SW-activate path rejects, or neither settles nor reloads (a failed SW
  // install used to hang its statechange await forever), the 12s timer
  // forces the plain reload — cleared as soon as the promise settles, so a
  // healthy path never double-reloads.
  const fallbackTimer = setTimeout(() => deps.reload(), 12_000)
  void deps.recoverStaleBundle()
    .then((recovered) => {
      clearTimeout(fallbackTimer)
      if (!recovered) deps.reload()
    })
    .catch(() => {
      clearTimeout(fallbackTimer)
      deps.reload()
    })
  return 'reloading'
}

// Default browser-backed deps for App.tsx — keeps the SUT wiring in one place.
export function browserChunkReloadDeps(recoverStaleBundle: () => Promise<boolean>): ChunkReloadDeps {
  return {
    now: () => Date.now(),
    online: () => typeof navigator === 'undefined' || navigator.onLine !== false,
    getReloadAt: () => sessionStorage.getItem(RELOAD_AT_KEY),
    setReloadAt: (v) => sessionStorage.setItem(RELOAD_AT_KEY, v),
    clearReloadAt: () => sessionStorage.removeItem(RELOAD_AT_KEY),
    reload: () => window.location.reload(),
    recoverStaleBundle,
  }
}
